'use client'

import { useIsActiveHideVatPrice } from '@/feature/product/hooks/useIsActiveHideVatPrice'
import { useIsAuthenticated } from '@/lib/auth/client/AuthProvider.client'
import { formatPrice } from '@/lib/formatters/currency.formatter'
import { HiddenTransNode } from '@/lib/i18n/HiddenTransNode'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { Unit } from '@gql-generated/graphql'
import type React from 'react'
import { ProductPriceIncludedTaxLink } from '../product-price-included-tax/ProductPriceIncludedTaxLink.client'
import type { ProductPriceProps } from './ProductPrice.client'
import { ProductPriceIncludedTax } from './ProductPriceIncludedTax.client'

export type ProductPriceDisplayProps = StrictOmit<
  ProductPriceProps,
  'discount' | 'cartItemDiscount' | 'type' | 'size' | 'className' | 'wrapperClassName'
> & {
  priceClass: string
  wrapperClass: string
}

// Wraps inclTaxPrice and appends the link
function InclTaxWrapper({ children }: { children?: React.ReactNode }) {
  return (
    <span className="text-sm font-medium text-brand-2-900">
      {children} <ProductPriceIncludedTaxLink />
    </span>
  )
}

export function ProductPriceDisplay({
  price,
  unitPrice,
  priceQuantity,
  inclTaxPrice,
  packaging,
  noUnits,
  priceClass,
  wrapperClass,
}: ProductPriceDisplayProps): React.ReactElement {
  const { Trans, t } = useTranslation(['generic', 'productPrice'])
  const isAuthenticated = useIsAuthenticated()
  const isActiveHideVatPrice = useIsActiveHideVatPrice()
  const erpQuantity = packaging?.quantity ?? 1
  const quantity = priceQuantity ?? erpQuantity
  const unit = packaging?.unit ?? Unit.P
  const unitLabel = t(`generic:units.${unit}`, { count: quantity })
  const erpUnitLabel = t(`generic:units.${unit}`, { count: erpQuantity })
  const hasUnitPrice = !!unitPrice && unitPrice > 0
  const hasInclTaxPrice =
    !!inclTaxPrice && inclTaxPrice > 0 && !isAuthenticated && !isActiveHideVatPrice

  const i18nKey =
    !!priceQuantity && hasUnitPrice
      ? 'priceAndUnitsWithErpPrice'
      : hasUnitPrice || hasInclTaxPrice
        ? 'priceAndUnitsWithUnitPrice'
        : noUnits || !quantity
          ? 'priceOnly'
          : 'priceAndUnits'

  const formatPriceWithExclTax = (amount: number) =>
    isActiveHideVatPrice ? formatPrice(amount) : t('productPrice:priceExclTax', { value: amount })
  const priceWithExclTax = formatPriceWithExclTax(price)

  if (unitPrice === 0) {
    return (
      <p className={wrapperClass} data-testid="product-price/price">
        <Trans
          i18nKey="productPrice:priceOnly"
          values={{ price: priceWithExclTax }}
          components={{
            price: <span className={priceClass} />,
          }}
        />
        <span className="flex flex-col leading-light">
          <span className="text-sm font-medium text-brand-2-900">
            / {quantity} {unitLabel}
          </span>
          {hasInclTaxPrice && <ProductPriceIncludedTax />}
        </span>
      </p>
    )
  }

  return (
    <p className={wrapperClass} data-testid="product-price/price">
      <Trans
        i18nKey={`productPrice:${i18nKey}`}
        values={{
          price: priceWithExclTax,
          nbUnit: quantity,
          textUnit: unitLabel,
          erpNbUnit: erpQuantity,
          erpTextUnit: erpUnitLabel,
          priceSingleUnit: hasUnitPrice ? formatPriceWithExclTax(unitPrice || 0) : '',
          inclTaxPrice: hasInclTaxPrice ? formatPrice(inclTaxPrice || 0) : '',
        }}
        components={{
          price: <span className={priceClass} />,
          wrapper: <span className="flex flex-col" />,
          units: <span className="text-sm font-medium text-brand-2-900" />,
          line1: <span className="inline-flex items-baseline gap-1" />,
          erpPrice: <span className="text-sm font-medium text-brand-2-800" />,
          inclTaxPrice: isActiveHideVatPrice ? (
            <HiddenTransNode />
          ) : hasInclTaxPrice ? (
            <InclTaxWrapper />
          ) : (
            <span />
          ),
        }}
      />
    </p>
  )
}
